import { useEffect, useRef, useState } from 'react'
import {
  CloseButton,
  Box,
  Button,
  Field,
  HStack,
  Input,
  NativeSelect,
  Portal,
  Text,
  VStack,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { Card } from '../shared/Card'
import { StatusChip } from '../shared/StatusChip'
import { LegiScanCredit } from './LegiScanCredit'
import { billStatusVariant } from './billStatus'
import { useBillSearch, useLinkBill, useUnlinkBill, useRefreshBills } from '../../hooks/useLobbyBills'
import { useLobbyDayEvent, useBillSessions } from '../../hooks/useLobbyDay'
import { useAvailableStates } from '../../hooks/useAvailableStates'
import type { AskBill, BillSearchCandidate, OfficeLevel } from '../../types/lobbyDay'

interface BillLinkModalProps {
  eventId: string
  askId: string
  open: boolean
  onClose: () => void
  /** Optional: lets a host (e.g. AskEditorCard) mirror the modal's linked-bill list for a
   * compact summary without duplicating the refresh/link/unlink bookkeeping. */
  onLinkedBillsChange?: (bills: AskBill[]) => void
  /** Optional: fired with the single bill that was just linked (not the whole list), so a host
   * can react to that one bill -- e.g. seed the ask's "Bill" tab from its summary. */
  onBillLinked?: (bill: AskBill) => void
  /** Optional override for the hosting event's resolved jurisdiction
   * (LobbyDayEventDetailDto.state/.level), so the modal's "State" box can show what LegiScan
   * search will actually scope to instead of a static "Auto-detected" placeholder. Most hosts
   * don't have the event detail in scope (they only hold `eventId`) -- when omitted, the modal
   * self-sources the same fields via `useLobbyDayEvent(eventId)`. */
  eventState?: string | null
  eventLevel?: OfficeLevel | null
}

// Mirrors the backend LegiScanMapping.NormalizeBillQuery: uppercase, keep only letters/digits
// (drops spaces and dashes), then strip leading zeros from the trailing digit run. Keeps the FE's
// exact-match detection in step with the query the backend actually sends to LegiScan, so a bill
// typed "HB-642" / "HB 642" / "HB0642" is recognized as the canonical "HB642" rather than demoted
// to the fuzzy "Did you mean?" list.
function normalizeBillNumber(s: string) {
  const alnum = s.toUpperCase().replace(/[^A-Z0-9]/g, '')
  const digitStart = alnum.search(/\d+$/)
  if (digitStart === -1) return alnum // no trailing digit run
  const prefix = alnum.slice(0, digitStart)
  const digits = alnum.slice(digitStart).replace(/^0+/, '')
  return prefix + (digits === '' ? '0' : digits) // an all-zero run still means "bill 0"
}

function BillCandidateCard({
  candidate, onLink, linking,
}: { candidate: BillSearchCandidate; onLink: (c: BillSearchCandidate) => void; linking: boolean }) {
  return (
    <Card p={3}>
      <HStack justify="space-between" align="flex-start" gap={3}>
        <Box>
          <Text fontWeight="bold" fontSize="sm">{candidate.billNumber}</Text>
          <Text fontSize="sm">{candidate.title}</Text>
          <Box mt={1}>
            <StatusChip variant={billStatusVariant(candidate.statusLabel)}>
              {candidate.statusLabel ?? 'Status unknown'}
            </StatusChip>
          </Box>
        </Box>
        <Button size="sm" onClick={() => onLink(candidate)} loading={linking}>
          Link
        </Button>
      </HStack>
    </Card>
  );
}

export function BillLinkModal({
  eventId, askId, open, onClose, onLinkedBillsChange, onBillLinked, eventState, eventLevel,
}: BillLinkModalProps) {
  const [billNumber, setBillNumber] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [linkedBills, setLinkedBills] = useState<AskBill[]>([])
  const [selectedSessionId, setSelectedSessionId] = useState<number | undefined>(undefined)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data: sessions } = useBillSessions(eventId)
  useEffect(() => {
    if (selectedSessionId === undefined && sessions?.length) {
      setSelectedSessionId((sessions.find((s) => s.isDefault) ?? sessions[0]).sessionId)
    }
    // Only seed the default once sessions arrive -- never overwrite a user's own pick.
  }, [sessions, selectedSessionId])

  const { data: candidates, isFetching: isSearching } = useBillSearch(eventId, askId, searchTerm, selectedSessionId)
  const linkMutation = useLinkBill(eventId, askId)
  const unlinkMutation = useUnlinkBill(eventId, askId)
  const refreshMutation = useRefreshBills(eventId, askId)
  const { data: availableStates } = useAvailableStates()
  // Most hosts (AskEditorCard, AskReconciliationSection) only have `eventId` in scope, not the
  // full event detail -- self-source state/level here rather than prop-drilling it through them.
  // Explicit props still win, so a future host with the event object already in scope can pass
  // it directly and skip the extra fetch.
  const { data: event } = useLobbyDayEvent(eventId)
  const resolvedState = eventState ?? event?.state ?? undefined
  const resolvedLevel = eventLevel ?? event?.level ?? undefined

  const jurisdictionResolvable = resolvedLevel === 'Federal' || !!resolvedState
  const jurisdictionLabel = resolvedLevel === 'Federal'
    ? 'US Congress'
    : resolvedState
      ? (availableStates?.find((s) => s.code === resolvedState)?.name ?? resolvedState)
      : 'Not set'

  const setAndReportLinkedBills = (bills: AskBill[]) => {
    setLinkedBills(bills)
    onLinkedBillsChange?.(bills)
  }

  // There is no standalone GET for an ask's linked bills -- RefreshBills's response IS that
  // list (it returns every LobbyDayAskBill row for the ask, refreshed against LegiScan where
  // stale). Hydrate + refresh in one call whenever the modal opens.
  useEffect(() => {
    if (!open) return
    setBillNumber('')
    setSearchTerm('')
    refreshMutation.mutateAsync()
      .then(setAndReportLinkedBills)
      .catch(() => toaster.create({ type: 'error', title: 'Failed to load linked bills' }))
    // Only re-run when the modal opens for a (possibly new) ask -- not on every mutation
    // object identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId, askId])

  const results = candidates ?? []
  const hasSearched = searchTerm.trim() !== ''
  const normalizedInput = normalizeBillNumber(searchTerm)
  // Surface the bill whose number matches what was typed even when LegiScan's full-text search
  // returns many hits -- the old `results.length === 1` guard buried the exact bill in the
  // "Did you mean?" list on every real (multi-hit) search.
  const exactMatch = hasSearched
    ? results.find((r) => normalizeBillNumber(r.billNumber) === normalizedInput)
    : undefined
  // The remaining hits, with the exact match (already shown above) removed.
  const otherResults = exactMatch ? results.filter((r) => r.billId !== exactMatch.billId) : results
  const showDidYouMean = hasSearched && otherResults.length > 0

  const handleFind = () => {
    const trimmed = billNumber.trim()
    if (!trimmed) return
    setSearchTerm(trimmed)
  }

  const handleLink = async (candidate: BillSearchCandidate) => {
    try {
      const linked = await linkMutation.mutateAsync({ legiScanBillId: candidate.billId })
      setAndReportLinkedBills([...linkedBills, linked])
      onBillLinked?.(linked)
      setBillNumber('')
      setSearchTerm('')
      toaster.create({ type: 'success', title: `${linked.billNumber} linked` })
    } catch {
      toaster.create({ type: 'error', title: 'Failed to link bill' })
    }
  }

  const handleRemove = async (askBill: AskBill) => {
    try {
      await unlinkMutation.mutateAsync(askBill.id)
      setAndReportLinkedBills(linkedBills.filter((b) => b.id !== askBill.id))
    } catch {
      toaster.create({ type: 'error', title: 'Failed to remove bill' })
    }
  }

  const handleAddAnother = () => {
    setBillNumber('')
    setSearchTerm('')
    inputRef.current?.focus()
  }

  return (
    <Dialog.Root open={open} size="lg" onOpenChange={(e) => { if (!e.open) onClose() }}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Link bills to this ask</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={4} align="stretch">
                <HStack gap={4} align="flex-start">
                  <Field.Root maxW="130px">
                    <Field.Label fontSize="xs">State</Field.Label>
                    <Input size="sm" value={jurisdictionLabel} readOnly bg="surface.2" />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="xs">Session</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        aria-label="Session"
                        value={selectedSessionId ?? ''}
                        onChange={(e) => setSelectedSessionId(Number(e.target.value))}
                      >
                        {sessions?.map((s) => (
                          <option key={s.sessionId} value={s.sessionId}>
                            {s.label}{s.isDefault ? ' (default)' : ''}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                </HStack>

                <Field.Root>
                  <Field.Label fontSize="xs">Bill number</Field.Label>
                  <HStack gap={2}>
                    <Input
                      ref={inputRef}
                      size="sm"
                      value={billNumber}
                      onChange={(e) => setBillNumber(e.target.value)}
                      placeholder="HB 1234"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleFind}
                      loading={isSearching}
                      disabled={!billNumber.trim()}
                    >
                      Find
                    </Button>
                  </HStack>
                </Field.Root>

                {exactMatch && (
                  <BillCandidateCard candidate={exactMatch} onLink={handleLink} linking={linkMutation.isPending} />
                )}

                {showDidYouMean && (
                  <VStack align="stretch" gap={2}>
                    <Text fontSize="xs" color="muted">Did you mean?</Text>
                    {otherResults.map((c) => (
                      <BillCandidateCard key={c.billId} candidate={c} onLink={handleLink} linking={linkMutation.isPending} />
                    ))}
                  </VStack>
                )}

                {hasSearched && !isSearching && results.length === 0 && (
                  <Text fontSize="sm" color="muted">
                    {jurisdictionResolvable
                      ? <>No bills found for &quot;{searchTerm}&quot;.</>
                      : "Set this event's state to search for bills."}
                  </Text>
                )}

                <Text fontSize="xs" color="muted">Not the right bill? Edit the number and Find again.</Text>

                <Box>
                  <Text fontSize="xs" color="muted" mb={2}>Linked to this ask</Text>
                  {linkedBills.length === 0 && (
                    <Text fontSize="sm" color="muted" mb={2}>No bills linked yet.</Text>
                  )}
                  <VStack align="stretch" gap={2}>
                    {linkedBills.map((b) => (
                      <HStack
                        key={b.id}
                        justify="space-between"
                        align="flex-start"
                        borderWidth="1px"
                        borderColor="border"
                        borderRadius="md"
                        p={2}
                        gap={3}
                      >
                        <Box>
                          <HStack gap={1} align="baseline">
                            <Text fontWeight="bold" fontSize="sm">{b.billNumber}</Text>
                            {b.title && <Text fontSize="sm" color="muted">· {b.title}</Text>}
                          </HStack>
                          <Box mt={1}>
                            <StatusChip variant={billStatusVariant(b.statusLabel)}>
                              {b.statusLabel ?? 'Status unknown'}
                            </StatusChip>
                          </Box>
                        </Box>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => handleRemove(b)}
                          loading={unlinkMutation.isPending}
                        >
                          Remove
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                  <Button size="sm" variant="ghost" mt={2} onClick={handleAddAnother}>
                    + Add another bill
                  </Button>
                </Box>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer justifyContent="space-between" alignItems="center">
              <LegiScanCredit />
              <HStack>
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button colorPalette="brand" onClick={onClose}>Done</Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
